import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import { specialtiesAPI, Specialty } from '../../services/api';

// Admin screen to manage the organization's specialty list. Writes are
// admin-gated on the backend; this UI assumes an admin reaches it via the nav.
const SpecialtyManagement: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    specialtiesAPI
      .getAll()
      .then((r) => setSpecialties(r.data || []))
      .catch(() => setSpecialties([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await specialtiesAPI.create(name);
      setNewName('');
      load();
    } catch (err: any) {
      showToast(
        err.response?.data?.error ||
          t('skills.saveFailed', { defaultValue: 'Could not save skill.' }),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (id: number) => {
    const name = editingName.trim();
    if (!name) return;
    try {
      await specialtiesAPI.update(id, name);
      setEditingId(null);
      setEditingName('');
      load();
    } catch (err: any) {
      showToast(
        err.response?.data?.error ||
          t('skills.saveFailed', { defaultValue: 'Could not save skill.' }),
        'error',
      );
    }
  };

  const handleDelete = async (spec: Specialty) => {
    const ok = await confirm({
      title: t('skills.deleteTitle', { defaultValue: 'Delete skill' }),
      message: t('skills.deleteConfirm', {
        name: spec.name,
        defaultValue: `Delete "${spec.name}"? It will be removed from all users.`,
      }),
    });
    if (!ok) return;
    try {
      await specialtiesAPI.delete(spec.id);
      load();
    } catch (err: any) {
      showToast(
        err.response?.data?.error ||
          t('skills.deleteFailed', {
            defaultValue: 'Could not delete skill.',
          }),
        'error',
      );
    }
  };

  const card = isDark
    ? 'bg-slate-900/60 border-slate-700'
    : 'bg-white border-slate-200';
  const input = isDark
    ? 'bg-slate-900 border-slate-700 text-white'
    : 'bg-white border-slate-300 text-slate-900';
  const rowText = isDark ? 'text-slate-200' : 'text-slate-800';

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <h1
        className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
      >
        {t('skills.title', { defaultValue: 'Skills' })}
      </h1>

      <form
        onSubmit={handleCreate}
        className={`flex gap-2 p-4 rounded-xl border ${card}`}
      >
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={t('skills.newPlaceholder', {
            defaultValue: 'New Skill (e.g. Machine Learning)',
          })}
          className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${input}`}
        />
        <button
          type="submit"
          disabled={saving || !newName.trim()}
          className="px-4 py-2 text-sm font-medium bg-violet-600 hover:bg-violet-500 text-white rounded-lg disabled:opacity-50"
        >
          {t('common.add', { defaultValue: 'Add' })}
        </button>
      </form>

      <div
        className={`rounded-xl border ${card} divide-y ${isDark ? 'divide-slate-700' : 'divide-slate-200'}`}
      >
        {loading ? (
          <div className="p-4 text-sm text-slate-500">
            {t('common.loading')}
          </div>
        ) : specialties.length === 0 ? (
          <div className="p-4 text-sm text-slate-500">
            {t('skill.empty', { defaultValue: 'No skill yet.' })}
          </div>
        ) : (
          specialties.map((spec) => (
            <div
              key={spec.id}
              className="flex items-center justify-between gap-3 p-3"
            >
              {editingId === spec.id ? (
                <input
                  autoFocus
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleRename(spec.id)}
                  className={`flex-1 px-2 py-1 border rounded-lg text-sm ${input}`}
                />
              ) : (
                <span className={`text-sm ${rowText}`}>
                  {spec.name}
                  {typeof spec.user_count === 'number' && (
                    <span className="ml-2 text-xs text-slate-500">
                      ({spec.user_count})
                    </span>
                  )}
                </span>
              )}
              <div className="flex items-center gap-2 flex-shrink-0">
                {editingId === spec.id ? (
                  <>
                    <button
                      onClick={() => handleRename(spec.id)}
                      className="text-sm font-medium text-violet-400 hover:text-violet-300"
                    >
                      {t('common.save')}
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setEditingName('');
                      }}
                      className="text-sm text-slate-400 hover:text-slate-300"
                    >
                      {t('common.cancel')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setEditingId(spec.id);
                        setEditingName(spec.name);
                      }}
                      className="text-sm text-slate-400 hover:text-slate-200"
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={() => handleDelete(spec)}
                      className="text-sm text-rose-400 hover:text-rose-300"
                    >
                      {t('common.delete')}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SpecialtyManagement;
